import React, { useEffect, useState } from 'react';
import { Box, Button, FormControlLabel, Radio, RadioGroup, TextField, Typography, useTheme } from '@mui/material';
import { checkUsernameAPI, postSignupAPI } from 'apis/auth/authAPI';
import { useSignupStore } from 'store/useSignupStore';
import './SignUpEnterInform.css';
import { formatCompanyPhoneNumber, formatPhoneNumber, isValidateEmail, isValidatePassword, isValidPhoneNumber } from 'constants/regularExpression';
import { postEmailAPI, postEmailConfirmAPI, postPhoneAPI, postPhoneConfirmAPI } from 'apis/verify/verifyAPI';
import ScrollToTop from '../ScrollToTop';
import dayjs, { Dayjs } from 'dayjs';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import usePolicyStore from '../../store/usePolicyStore';
import { useTranslation } from 'react-i18next';
import SnackbarAlert from 'components/SnackbarAlert';
import { useSnackbarStore } from 'store/useSnackbarStore';

// SignUpAgreeTerms 컴포넌트에 사용할 props 타입 정의
interface SignUpEnterInformProps {
  handleNext: () => void;
  handleBack: () => void;
}

export default function SignUpEnterInform({ handleNext, handleBack }: SignUpEnterInformProps) {
  const { termsPolicyId, privacyPolicyId } = usePolicyStore(); // Zustand에서 가져옴 : 서버에서 받아온 기존 이용약관 및 개인정보처리방침
  const [userType, setUserType] = useState('NATIVE'); // 사용자의 내/외국인 타입 상태 추가 (이메일, 전화번호 인증버튼에 필요)
  //아이디
  const [idSuccess, setIdSuccess] = useState(false);
  const [idAlertMessage, setIdAlertMessage] = useState('');
  const [id, setId] = useState('');
  const [idTouched, setIdTouched] = useState(false); // 사용자가 아이디 필드를 벗어났는지 여부
  // 비밀번호 및 상태 관리
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordAlert, setPasswordAlert] = useState(true); // 비밀번호 유효성 상태
  const [confirmPasswordAlert, setConfirmPasswordAlert] = useState(true); // 비밀번호 일치 여부
  const [passwordTouched, setPasswordTouched] = useState(false); // 비밀번호 확인 필드가 포커스를 벗어났는지 여부
  // 생년월일
  const [birth, setBirth] = useState<string>(''); // 초기값을 빈 문자열로 설정
  // 이메일 상태와 이메일 검증 결과 상태
  const [email, setEmail] = useState('');
  const [emailAlert, setEmailAlert] = useState(true); // 이메일 유효성 상태 (기본값 true)
  const [emailTouched, setEmailTouched] = useState(false); // 사용자가 이메일 필드를 벗어났는지 여부
  const [emailInputDisabled, setEmailInputDisabled] = useState(false); // 이메일 필드 disabled
  const [isEmailCertifyBtnEnabled, setIsEmailCertifyBtnEnabled] = useState(false); // 이메일 인증 버튼 활성화 상태
  // 휴대전화 상태관리 (인증버튼에 필요)
  const [phoneNumber, setPhoneNumber] = useState('');
  const [phoneNumberAlert, setPhoneNumberAlert] = useState(true); //  휴대전화 유효성 상태 (기본값 true)
  const [phoneNumberTouched, setPhoneNumberTouched] = useState(false); // 사용자가  휴대전화 필드를 벗어났는지 여부
  const [isPhoneCertifyBtnEnabled, setIsPhoneCertifyBtnEnabled] = useState(false); //  휴대전화 인증 버튼 활성화 상태

  const [isNationToggleDisabled, setIsNationToggleDisabled] = useState(false); // 내외국인 토글 활성화 상태
  // 회사연락처 상태관리
  const [companyPhoneNumber, setCompanyPhoneNumber] = useState('');
  // 이메일 및 휴대전화 인증 상태관리
  const [isEmailVerified, setIsEmailVerified] = useState(false); // 이메일 코드 완료상태
  const [isPhoneVerified, setIsPhoneVerified] = useState(false); // 휴대전화 코드 완료상태
  const [emailVerificationCode, setEmailVerificationCode] = useState(''); // 이메일 코드 상태관리
  const [phoneVerificationCode, setPhoneVerificationCode] = useState(''); // 휴대전화 코드 상태관리
  const [emailVerificationStep, setEmailVerificationStep] = useState(false); // 이메일 코드 입력창 보여주는 여부
  const [phoneVerificationStep, setPhoneVerificationStep] = useState(false); // 휴대전화 코드 입력창 보여주는 여부
  // 다크 라이트 모드
  const theme = useTheme();
  // 인증시간 : 3분 (180초)
  const [remainingTime, setRemainingTime] = useState(180);
  // 다국어
  const { t } = useTranslation();
  // 스낵바
  const showSnackbar = useSnackbarStore((state) => state.showSnackbar);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const data = {
      username: formData.get('username')?.toString() || id || '',
      password: formData.get('password')?.toString() || password || '',
      type: formData.get('type')?.toString() || userType || '',
      name: formData.get('name')?.toString() || '',
      birth: formData.get('birth')?.toString(),
      email: formData.get('email')?.toString() || email || '',
      phoneNumber: formData.get('phoneNumber')?.toString() || phoneNumber || '',
      companyName: formData.get('companyName')?.toString() || '',
      companyPhoneNumber: formData.get('companyPhoneNumber')?.toString() || '',
      agreeToTerms: true,
      termsPolicyId: termsPolicyId,
      agreeToPrivacyPolicy: true,
      privacyPolicyId: privacyPolicyId
    };

    try {
      const response = await postSignupAPI(data);
      if (response) {
        // store에 username과 name 저장
        const { setSignupData } = useSignupStore.getState();
        setSignupData(data.username, data.name);
        // 다음단계로 이동
        handleNext();
      }
    } catch (error) {
      console.error('API 호출 중 오류 발생:', error);
      const errorMessage = (error as { data: { message: '' } })?.data?.message || t('sign_up_enter_inform.message.sign_up_err_msg');
      showSnackbar(`${errorMessage}`, 'error');
    }
  };

  // 이메일 입력 시 상태 업데이트
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
  };

  // 이메일 필드에서 포커스를 벗어날 때 호출되는 함수
  const handleEmailBlur = () => {
    setEmailTouched(true); // 사용자가 필드를 떠났다는 표시
    setEmailAlert(isValidateEmail(email)); // 유효성 검사를 실행
  };

  //  휴대전화
  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const phoneNumber = event.target.value?.replace(/[^0-9]/g, '');
    setPhoneNumber(formatPhoneNumber(phoneNumber));
  };
  // 회사연락처
  const handleCompanyPhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const companyPhoneNumber = event.target.value?.replace(/[^0-9]/g, '');
    setCompanyPhoneNumber(formatCompanyPhoneNumber(companyPhoneNumber));
  };

  // 휴대폰 필드에서 포커스를 벗어날 때 호출되는 함수
  const handlePhoneNumberBlur = () => {
    setPhoneNumberTouched(true); // 사용자가 필드를 떠났다는 표시
    setPhoneNumber(formatPhoneNumber(phoneNumber));
    setPhoneNumberAlert(isValidPhoneNumber(phoneNumber)); // 유효성 검사를 실행
  };

  // 회사연락처 필드에서 포커스를 벗어날 때 호출되는 함수
  const handleCompanyPhoneNumberBlur = () => {
    setCompanyPhoneNumber(formatCompanyPhoneNumber(companyPhoneNumber));
  };

  // 아이디 필드에서 포커스를 벗어날 때 호출되는 함수
  const handleIdBlur = async () => {
    setIdTouched(true); // 사용자가 필드를 떠났다는 표시
    // const validateIdisTrue = isValidateId(id);
    // setIdAlert(validateIdisTrue); // 유효성 검사를 실행
    // validateIdisTrue 이고, id가 입력된거면, 중복검사 API 실행
    if (id) {
      try {
        const response = await checkUsernameAPI(id); // API 호출 결과를 기다림
        // API 호출 결과에 따른 처리 (예: 중복 여부 상태 설정)
        if (response.code === 200) {
          setIdSuccess(true);
          setIdAlertMessage('');
          setIdTouched(false);
        }
      } catch (error) {
        setIdSuccess(false);
        setIdAlertMessage((error as { data: { message: '' } }).data?.message);
        console.error('API 호출 에러:', error);
      }
    } else {
      setIdSuccess(false);
    }
  };

  // 휴대전화 인증버튼 클릭시 호출되는 함수 수정
  const handlePhoneVerify = async () => {
    try {
      const response = await postPhoneAPI({ phoneNumber }); // phoneNumber를 넘겨 인증코드 받는 API호출
      if (response.data.code === 200) {
        setPhoneVerificationStep(true); // 인증코드 칸 보여주는 상태관리
        setRemainingTime(180); // 3분으로 타이머 초기화
        setIsPhoneCertifyBtnEnabled(false); // 인증버튼 비활성화
        setIsNationToggleDisabled(true); // 내/외국인 토글 비활성화
      }
    } catch (error) {
      const { data } = error as { data: { message: '' } };
      showSnackbar(`${data.message}`, 'error');
      console.error('휴대폰 인증 API 호출 에러:', error);
    }
  };

  // 이메일 인증버튼 클릭시 호출되는 함수 수정
  const handleEmailVerify = async () => {
    try {
      const response = await postEmailAPI({ email }); // 이메일 인증코드 보내는 API
      if (response.data.code === 200) {
        setEmailVerificationStep(true); // 코드 입력창 보여줌
        setRemainingTime(180); // 3분으로 타이머 초기화
        setIsEmailCertifyBtnEnabled(false); // 인증버튼 비활성화
        setIsNationToggleDisabled(true); // 내/외국인 토글 비활성화
        setEmailInputDisabled(true);
      }
    } catch (error) {
      const { data } = error as { data: { message: '' } };
      showSnackbar(`${data.message}`, 'error');
      console.error('휴대폰 인증 API 호출 에러:', error);
    }
  };

  // 휴대전화 인증 코드 입력에서 입력값이 변경될 때
  const handlePhoneVerificationCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneVerificationCode(event.target.value);
  };

  // 이메일 인증 코드 입력에서 입력값이 변경될 때
  const handleEmailVerificationCodeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmailVerificationCode(event.target.value);
  };

  // 이메일 인증 코드 확인 버튼 클릭
  const handleEmailCodeSubmit = async () => {
    try {
      const response = await postEmailConfirmAPI({ email: email, verificationCode: emailVerificationCode });
      if (response) {
        showSnackbar(t('sign_up_enter_inform.message.email_certify_success_content'), 'success');
        setEmailVerificationStep(false); // 인증 성공 시 인증 단계 비활성화
        setEmailVerificationCode(''); // 인증코드 초기화
        setIsEmailVerified(true); // 인증상태 true - Input 비활성화하는데 필요
      }
    } catch (error) {
      console.error('이메일 인증 코드 확인 에러:', error);
      const { data } = error as { data: { message: '' } };
      // Modal.error({ title: t('sign_up_enter_inform.message.email_certify_wrong'), content: `${data.message}` });
      // showSnackbar(t('sign_up_enter_inform.message.email_certify_wrong'), 'error');
      showSnackbar(`${data.message}`, 'error');
    }
  };
  // 휴대전화 인증 확인버튼 클릭시 호출되는 함수
  const handlePhoneCodeSubmit = async () => {
    try {
      const response = await postPhoneConfirmAPI({ phoneNumber, verificationCode: phoneVerificationCode });
      if (response) {
        showSnackbar(t('sign_up_enter_inform.message.phone_certify_success_content'), 'success');
        setPhoneVerificationStep(false); // 인증 코드 입력 단계 활성화
        setPhoneVerificationCode(''); // 인증코드 초기화
        setIsPhoneVerified(true); // 인증상태 true - Input 비활성화하는데 필요
      }
    } catch (error) {
      console.error('휴대전화 인증 코드 확인 에러:', error);
      const { data } = error as { data: { message: '' } };
      // Modal.error({ title: t('sign_up_enter_inform.message.phone_certify_wrong'), content: `${data.message}` });
      // showSnackbar(t('sign_up_enter_inform.message.phone_certify_wrong'), 'error');
      showSnackbar(`${data.message}`, 'error');
    }
  };

  // 비밀번호 입력 시 상태 업데이트 및 유효성 검사
  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputPassword = event.target.value;
    setPassword(inputPassword);
    setPasswordAlert(isValidatePassword(inputPassword)); // 유효성 검사 실행
  };
  // 비밀번호 확인 필드에서 포커스를 벗어날 때 호출
  const handleConfirmPasswordBlur = () => {
    setPasswordTouched(true); // 사용자가 필드를 벗어났음을 표시
    setConfirmPasswordAlert(password === confirmPassword); // 비밀번호가 일치하는지 확인
  };

  // 생년월일 변경 핸들러
  const handleDateChange = (newDate: Dayjs | null) => {
    if (newDate) {
      const today = dayjs();
      const minAgeDate = today.subtract(19, 'year'); // 만 19세 기준 날짜
      const isUnderAge = newDate.isAfter(minAgeDate); // 만 19세 미만 여부 확인

      if (isUnderAge) {
        // 만 19세 미만인 경우 에러 메시지 설정
        showSnackbar(t('visitor_info.message.birth_error'), 'error');
        setBirth(''); // 생년월일 초기화
      } else {
        setBirth(newDate.format('YYYY-MM-DD'));
      }
    } else {
      // 날짜가 선택되지 않은 경우 초기화

      setBirth('');
    }
  };

  // phonePrefix, phoneMid, phoneEnd 값이 변경될 때마다 유효성 검증 실행 (인증버튼 활성화에 필요)
  useEffect(() => {
    // 유효성 검증 및 상태 업데이트
    if (isValidPhoneNumber(phoneNumber)) {
      setIsPhoneCertifyBtnEnabled(true); // 휴대전화 인증버튼 활성화
    } else {
      setIsPhoneCertifyBtnEnabled(false); // 휴대전화 인증버튼 비활성화
    }
  }, [phoneNumber]);

  // 이메일 유효성 검사 후 인증 버튼 활성화 로직
  useEffect(() => {
    // 이메일이 유효할 경우에만 버튼 활성화
    if (isValidateEmail(email)) {
      setIsEmailCertifyBtnEnabled(true);
    } else {
      setIsEmailCertifyBtnEnabled(false);
    }
  }, [email]);

  // 모든 필드가 유효한지 확인 (필요한 유효성 조건에 따라 업데이트)- 가입하기 버튼에 사용
  const isFormValid = () => {
    if (userType === 'NATIVE' && id && idSuccess && password && confirmPassword && birth && email && phoneNumber && isPhoneVerified) {
      return false;
    } else if (userType === 'FOREIGNER' && id && idSuccess && password && confirmPassword && birth && email && isEmailVerified && phoneNumber) {
      return false;
    } else {
      return true;
    }
  };

  // 타이머가 동작하는 useEffect
  useEffect(() => {
    let timer: NodeJS.Timeout | undefined; // 'undefined'로 초기화하여 타이머 상태를 추적

    // remainingTime이 0보다 크고 phoneVerificationStep이나 emailVerificationStep가 true일 때 타이머 시작
    if ((remainingTime > 0 && phoneVerificationStep) || (remainingTime > 0 && emailVerificationStep)) {
      timer = setInterval(() => {
        setRemainingTime((prev) => prev - 1); // 1초마다 remainingTime을 1초씩 감소시킴
      }, 1000);
      // remainingTime이 0이면 타이머 종료 후 phoneVerificationStep을 false로 변경
    } else if (remainingTime === 0) {
      if (timer) clearInterval(timer); // 타이머가 있으면 clearInterval로 타이머 종료

      if (phoneVerificationStep) {
        setPhoneVerificationStep(false); // 타이머가 끝나면 인증 단계 종료
        setIsPhoneCertifyBtnEnabled(true); // 휴대전화 인증버튼 활성화
      }
      if (emailVerificationStep) {
        setEmailVerificationStep(false); // 타이머가 끝나면 인증 단계 종료
        setIsEmailCertifyBtnEnabled(true); // 이메일 인증버튼 활성화
      }
    }
    // cleanup 함수: 컴포넌트가 언마운트될 때 타이머를 클리어
    return () => {
      if (timer) clearInterval(timer); // 언마운트 시 타이머를 중지
    };
  }, [remainingTime, phoneVerificationStep, emailVerificationStep]);

  const formatTime = (timeInSeconds: number) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = timeInSeconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={localStorage.getItem('language') || 'ko'}>
      <SnackbarAlert />
      <React.Fragment>
        <ScrollToTop />
        <form onSubmit={handleSubmit}>
          <Box
            maxWidth="800px"
            minWidth="400px"
            mt={4}
            mx="auto"
            padding={4}
            borderRadius={1}
            bgcolor={theme.palette.mode === 'dark' ? '#333' : '#f8f8f8'}
            display="flex"
            flexDirection="column"
            gap={2}
          >
            {/* 회원정보 */}
            <Typography variant="h5" fontWeight="bold">
              {t('sign_up_enter_inform.enter_inform')}
            </Typography>
            {/* 내/외국인 */}
            <Box display="flex" flexDirection="column" gap={1} borderRadius={1} boxShadow={2} padding={4} bgcolor="#ffffff">
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={100} variant="caption">
                  {t('sign_up_enter_inform.is_native_foreigners')}
                </Typography>
                <RadioGroup
                  row
                  value={userType} // userType 상태 사용
                  onChange={(e) => setUserType(e.target.value)} // 상태 업데이트
                  name="type"
                >
                  <FormControlLabel
                    value="NATIVE"
                    control={<Radio size="small" disabled={isNationToggleDisabled} />}
                    label={t('sign_up_enter_inform.native')}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                  <FormControlLabel
                    value="FOREIGNER"
                    control={<Radio size="small" disabled={isNationToggleDisabled} />}
                    label={t('sign_up_enter_inform.foreigner')}
                    sx={{
                      '& .MuiFormControlLabel-label': {
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                </RadioGroup>
              </Box>
              {/* 아이디 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={100} variant="caption">
                  {t('sign_up_enter_inform.id')}
                </Typography>
                <TextField
                  required
                  name="username"
                  id="username"
                  type="text"
                  size="small"
                  placeholder={t('sign_up_enter_inform.placeholder.id')}
                  value={id || ''} // 빈 문자열로 초기화
                  onChange={(e) => setId(e.target.value)}
                  onBlur={handleIdBlur} // 포커스가 벗어났을 때 유효성 검사 실행
                  error={idTouched && !!idAlertMessage} // 필드가 떠난 후에만 에러 표시
                  helperText={idAlertMessage}
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': {
                      // 내부의 input 요소에 스타일 적용
                      height: '32px',
                      borderRadius: '4px',
                      '& input': {
                        height: '32px',
                        fontSize: '0.875rem',
                        padding: '0 8px'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      marginX: '8px'
                    },
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: idSuccess ? 'green' : 'default'
                      },
                      '&:hover fieldset': {
                        borderColor: idSuccess ? 'green' : 'default'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: idSuccess ? 'green' : 'default',
                        borderWidth: '2px'
                      }
                    }
                  }}
                />
              </Box>
              {/* 비밀번호 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={100} variant="caption">
                  {t('sign_up_enter_inform.pw')}
                </Typography>
                <TextField
                  required
                  name="password"
                  type="password"
                  size="small"
                  placeholder={t('sign_up_enter_inform.placeholder.pw')}
                  value={password}
                  onChange={handlePasswordChange}
                  onBlur={() => setPasswordTouched(true)}
                  error={passwordTouched && !passwordAlert} // 필드가 떠난 후에만 에러 표시
                  helperText={passwordTouched && !passwordAlert ? t('sign_up_enter_inform.message.pw_validity') : null}
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': {
                      // 내부의 input 요소에 스타일 적용
                      height: '32px',
                      borderRadius: '4px',
                      '& input': {
                        height: '32px',
                        fontSize: '0.875rem',
                        padding: '0 8px'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      marginX: '8px'
                    }
                  }}
                />
              </Box>
              {/* 비밀번호 확인 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={100} variant="caption">
                  {t('sign_up_enter_inform.pw_check')}
                </Typography>
                <TextField
                  required
                  type="password"
                  size="small"
                  placeholder={t('sign_up_enter_inform.placeholder.pw')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={handleConfirmPasswordBlur} // 포커스를 벗어날 때 유효성 검사 실행
                  error={passwordTouched && !confirmPasswordAlert} // 필드가 떠난 후에만 에러 표시
                  helperText={passwordTouched && !confirmPasswordAlert ? t('sign_up_enter_inform.message.pw_wrong') : null}
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': {
                      // 내부의 input 요소에 스타일 적용
                      height: '32px',
                      borderRadius: '4px',
                      '& input': {
                        height: '32px',
                        fontSize: '0.875rem',
                        padding: '0 8px'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      marginX: '8px'
                    }
                  }}
                />
              </Box>
              {/* 이름 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={100} variant="caption">
                  {t('sign_up_enter_inform.name')}
                </Typography>
                <TextField
                  required
                  name="name"
                  size="small"
                  placeholder={t('sign_up_enter_inform.placeholder.name')}
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': {
                      // 내부의 input 요소에 스타일 적용
                      height: '32px',
                      borderRadius: '4px',
                      '& input': {
                        height: '32px',
                        fontSize: '0.875rem',
                        padding: '0 8px'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      marginX: '8px'
                    }
                  }}
                />
              </Box>
              {/* 생년월일 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={100} variant="caption">
                  {t('sign_up_enter_inform.birth')}
                </Typography>
                <DatePicker
                  name="birth"
                  format="YYYY-MM-DD"
                  showDaysOutsideCurrentMonth
                  value={birth ? dayjs(birth) : null} // 빈 값일 경우 null로 처리
                  onChange={(newValue) => handleDateChange(newValue)}
                  slotProps={{
                    calendarHeader: {
                      format: 'YYYY-MM'
                    },
                    textField: { placeholder: t('datepicker_placeholder') || 'YYYY-MM-DD' }
                  }}
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': {
                      height: '32px',
                      borderRadius: '4px',
                      '& input': {
                        height: '32px',
                        fontSize: '0.875rem',
                        padding: '0 8px'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      marginX: '8px'
                    },
                    '& .MuiSvgIcon-root': {
                      fontSize: '1.25rem'
                    }
                  }}
                />
              </Box>
              {/* 이메일 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={100} variant="caption">
                  {t('sign_up_enter_inform.email')}
                </Typography>
                <TextField
                  required
                  name="email"
                  id="email"
                  type="email"
                  size="small"
                  placeholder={t('sign_up_enter_inform.placeholder.email')}
                  value={email}
                  onChange={handleEmailChange}
                  onBlur={handleEmailBlur}
                  error={emailTouched && !emailAlert}
                  helperText={emailTouched && !emailAlert ? t('sign_up_enter_inform.message.email_wrong') : null}
                  disabled={emailInputDisabled}
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': { height: '32px', borderRadius: '4px', '& input': { height: '32px', fontSize: '0.875rem', padding: '0 8px' } },
                    '& .MuiFormHelperText-root': { marginX: '8px' }
                  }}
                />
                {/* 인증버튼 - 외국인만 */}
                {userType === 'FOREIGNER' && (
                  <Button variant="contained" size="small" onClick={handleEmailVerify} disabled={!isEmailCertifyBtnEnabled} sx={{ borderRadius: '4px' }}>
                    {t('sign_up_enter_inform.certify')}
                  </Button>
                )}
              </Box>
              {/* 인증코드 */}
              {userType === 'FOREIGNER' && emailVerificationStep && (
                <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                  <Typography minWidth={100} variant="caption">
                    {t('sign_up_enter_inform.certify_code')}
                  </Typography>
                  <TextField
                    size="small"
                    placeholder={t('sign_up_enter_inform.placeholder.certify_code')}
                    value={emailVerificationCode}
                    onChange={handleEmailVerificationCodeChange}
                    sx={{
                      flexGrow: 1,
                      '& .MuiInputBase-root': { height: '32px', borderRadius: '4px', '& input': { height: '32px', fontSize: '0.875rem', padding: '0 8px' } },
                      '& .MuiFormHelperText-root': { marginX: '8px' }
                    }}
                    inputProps={{
                      inputMode: 'numeric', // 모바일에서 숫자키패드
                      pattern: '[0-9]*' // 숫자만 입력
                    }}
                  />
                  <Button variant="contained" size="small" onClick={handleEmailCodeSubmit} sx={{ borderRadius: '4px' }}>
                    {t('sign_up_enter_inform.ok')}
                  </Button>
                </Box>
              )}
              {/* 인증 코드 유효시간 - 타이머 표시 */}
              {emailVerificationStep && remainingTime > 0 && (
                <Typography variant="caption" color="primary" sx={{ marginLeft: 14 }}>
                  {t('sign_up_enter_inform.certify_code_time')}: {formatTime(remainingTime)}
                </Typography>
              )}
              {/* 인증이 완료되었습니다. 알림창 */}
              {userType === 'FOREIGNER' && isEmailVerified && (
                <Typography width={150} marginLeft={14} variant="caption" color="success">
                  {t('sign_up_enter_inform.message.certify_code_finish')}
                </Typography>
              )}
              {/* 휴대전화 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={100} variant="caption">
                  {t('sign_up_enter_inform.phone')}
                </Typography>
                <TextField
                  required
                  name="phoneNumber"
                  id="phoneNumber"
                  type="tel"
                  size="small"
                  placeholder={t('sign_up_enter_inform.placeholder.phone')}
                  value={phoneNumber}
                  InputProps={{
                    readOnly: isPhoneVerified // 인증된 후에만 readOnly 적용, disabled옵션을 사용하면 빈값을 던지는 현상이 있음
                  }}
                  onChange={handlePhoneNumberChange}
                  onBlur={handlePhoneNumberBlur}
                  error={phoneNumberTouched && !phoneNumberAlert}
                  helperText={phoneNumberTouched && !phoneNumberAlert ? '' : null}
                  inputProps={{
                    inputMode: 'numeric', // 모바일에서 숫자키패드
                    pattern: '^\\d{3}-\\d{3,4}-\\d{4}$' // 하이픈을 포함한 전화번호 형식
                  }}
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': { height: '32px', borderRadius: '4px', '& input': { height: '32px', fontSize: '0.875rem', padding: '0 8px' } },
                    '& .MuiFormHelperText-root': { marginX: '8px' }
                  }}
                />
                {/* 인증 - 내국인만 */}
                {userType === 'NATIVE' && (
                  <Button variant="contained" size="small" onClick={handlePhoneVerify} disabled={!isPhoneCertifyBtnEnabled} sx={{ borderRadius: '4px' }}>
                    {t('sign_up_enter_inform.certify')}
                  </Button>
                )}
              </Box>
              {/* 인증코드 */}
              {userType === 'NATIVE' && phoneVerificationStep && (
                <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                  <Typography minWidth={100} variant="caption">
                    {t('sign_up_enter_inform.certify_code')}
                  </Typography>
                  <TextField
                    size="small"
                    placeholder={t('sign_up_enter_inform.placeholder.certify_code')}
                    value={phoneVerificationCode}
                    onChange={handlePhoneVerificationCodeChange}
                    sx={{
                      flexGrow: 1,
                      width: '100px',
                      '& .MuiInputBase-root': { height: '32px', borderRadius: '4px', '& input': { height: '32px', fontSize: '0.875rem', padding: '0 8px' } },
                      '& .MuiFormHelperText-root': { marginX: '8px' }
                    }}
                    inputProps={{
                      inputMode: 'numeric', // 모바일에서 숫자키패드
                      pattern: '[0-9]*' // 숫자만 입력
                    }}
                  />
                  <Button variant="contained" size="small" onClick={handlePhoneCodeSubmit} sx={{ borderRadius: '4px' }}>
                    {t('sign_up_enter_inform.ok')}
                  </Button>
                </Box>
              )}
              {/* 인증 코드 유효시간 - 타이머 표시 */}
              {phoneVerificationStep && remainingTime > 0 && (
                <Typography variant="caption" color="primary" sx={{ marginLeft: 14 }}>
                  {t('sign_up_enter_inform.certify_code_time')}: {formatTime(remainingTime)}
                </Typography>
              )}
              {/* 인증이 완료되었습니다. 알림창 */}
              {userType === 'NATIVE' && isPhoneVerified && (
                <Typography width={150} marginLeft={12} variant="caption" color="success">
                  {t('sign_up_enter_inform.message.certify_code_finish')}
                </Typography>
              )}
              {/* 회사 이름 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={100} variant="caption">
                  {t('sign_up_enter_inform.company_name')}
                </Typography>
                <TextField
                  required
                  name="companyName"
                  size="small"
                  placeholder={t('sign_up_enter_inform.placeholder.company_name')}
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': {
                      // 내부의 input 요소에 스타일 적용
                      height: '32px',
                      borderRadius: '4px',
                      '& input': {
                        height: '32px',
                        fontSize: '0.875rem',
                        padding: '0 8px'
                      }
                    },
                    '& .MuiFormHelperText-root': {
                      marginX: '8px'
                    }
                  }}
                />
              </Box>
              {/* 회사연락처 */}
              <Box display="flex" flexDirection="row" alignItems="center" gap={1}>
                <Typography minWidth={100} variant="caption">
                  {t('sign_up_enter_inform.company_phone')}
                </Typography>
                <TextField
                  required
                  name="companyPhoneNumber"
                  id="companyPhoneNumber"
                  type="tel"
                  size="small"
                  placeholder={t('sign_up_enter_inform.placeholder.company_phone')}
                  value={companyPhoneNumber}
                  onChange={handleCompanyPhoneNumberChange}
                  onBlur={handleCompanyPhoneNumberBlur}
                  inputProps={{
                    inputMode: 'numeric', // 모바일에서 숫자키패드
                    pattern: '^\\d{3}-\\d{3,4}-\\d{4}$' // 하이픈을 포함한 전화번호 형식
                  }}
                  sx={{
                    flexGrow: 1,
                    width: '100px',
                    '& .MuiInputBase-root': { height: '32px', borderRadius: '4px', '& input': { height: '32px', fontSize: '0.875rem', padding: '0 8px' } },
                    '& .MuiFormHelperText-root': { marginX: '8px' }
                  }}
                />
              </Box>
            </Box>

            {/* 가입하기 버튼 */}
            <Button
              variant="contained"
              color="primary"
              type="submit"
              fullWidth
              sx={{ borderRadius: '4px', fontWeight: 'bold' }}
              disabled={isFormValid()} // 모든 필드가 유효한 경우에만 활성화
            >
              {t('sign_up_enter_inform.btn.join')}
            </Button>
          </Box>
        </form>

        {/* 이전 버튼 */}
        <Box sx={{ display: 'flex', flexDirection: 'row', pt: 2, width: '100%', mb: '-1px' }}>
          <Button variant="contained" color="primary" onClick={handleBack} sx={{ width: '100px', borderRadius: '4px' }}>
            {t('sign_up_enter_inform.btn.before')}
          </Button>
        </Box>
      </React.Fragment>
    </LocalizationProvider>
  );
}
